import React, { ChangeEvent, useState } from "react";
import {
  IUCNStatus,
  PopulationTrend,
  Species,
  SpeciesCategory,
} from "../common/ObjectTypes";
import { AddIcon, DeleteIcon } from "../common/ProjectIcons";
import TextInput from "../common/TextInput";
import SelectInput from "../common/SelectInput";
import { getCurrentUser } from "../../helpers/authHeader";

type AddEditSpeciesTypes = {
  species: Species;
  iucnStatuses: IUCNStatus[];
  populationTrends: PopulationTrend[];
  speciesCategories: SpeciesCategory[];
  errors: any;
  addSpecies: boolean;
  existingSpecies: boolean;
  onChangeSingleSelect: Function;
  onChange: Function;
  onSave: (event: React.FormEvent<HTMLFormElement>) => void;
  onSelectSpecies: Function;
  onCancelEdit: Function;
  onDelete: Function;
};

const AddEditSpecies = ({
  species,
  iucnStatuses,
  populationTrends,
  speciesCategories,
  errors,
  onChange,
  onSave,
  onCancelEdit,
  onChangeSingleSelect,
  onDelete,
  existingSpecies,
}: AddEditSpeciesTypes) => {
  const currentUser = getCurrentUser();
  const getStatusById = (id: any) => {
    return iucnStatuses.find((status) => status.id === id);
  };
  const defaultStatus = getStatusById(species.iucnStatusId);
  const getPopulationTrendById = (id: any) => {
    return populationTrends.find((trend) => trend.id === id);
  };
  const defaultTrend = getPopulationTrendById(species.populationTrendId);
  const getSpeciesCategoryById = (id: any) => {
    return speciesCategories.find((category) => category.id === id);
  };
  const defaultCategory = getSpeciesCategoryById(species.speciesCategoryId);

  return (
    <div className="w-full my-4">
      <form className="flex flex-col items-center" onSubmit={(e) => onSave(e)}>
        <h1 className="font-bold text-center text-4xl mb-6">
          {existingSpecies ? "Species Details" : "Add Species"}
        </h1>
        <div className="bg-grayScale-300 w-2/3">
          <div className="flex flex-col py-2">
            <div className="px-6">
              {/* name */}
              <div className="pb-2">
                <TextInput
                  name={"name"}
                  label={"Name"}
                  placeholder={""}
                  value={species.name}
                  error={errors.name}
                  disabled={!currentUser.isSystemAdmin}
                  className={"bg-white-800 p-2 w-full rounded shadow"}
                  type={"text"}
                  initialFocus={true}
                  onChange={(e: any) => onChange(e)}
                />
                <TextInput
                  name={"description"}
                  label={"Description"}
                  placeholder={""}
                  value={species.description || ""}
                  error={""}
                  disabled={!currentUser.isSystemAdmin}
                  className={"bg-white-800 p-2 w-full rounded shadow"}
                  type={"text"}
                  initialFocus={true}
                  onChange={(e: any) => onChange(e)}
                />
                <TextInput
                  name={"colloquialName"}
                  label={"Colloquial Species Name"}
                  placeholder={""}
                  value={species.colloquialName || ""}
                  error={""}
                  disabled={!currentUser.isSystemAdmin}
                  className={"bg-white-800 p-2 w-full rounded shadow"}
                  type={"text"}
                  initialFocus={true}
                  onChange={(e: any) => onChange(e)}
                />
                <TextInput
                  name={"scientificName"}
                  label={"Scientific Species Name"}
                  placeholder={""}
                  value={species.scientificName || ""}
                  error={""}
                  disabled={!currentUser.isSystemAdmin}
                  className={"bg-white-800 p-2 w-full rounded shadow"}
                  type={"text"}
                  initialFocus={true}
                  onChange={(e: any) => onChange(e)}
                />
                {/* what if there's not a default status or trend? */}
                <SelectInput
                  name="iucnStatus"
                  label="Select IUCN Status"
                  options={iucnStatuses.map((status: any) => ({
                    value: status.id,
                    label: status.name,
                  }))}
                  onChange={(e: any) => onChangeSingleSelect("iucnStatusId", e)}
                  className="w-full"
                  defaultValue={
                    defaultStatus
                      ? {
                          value: defaultStatus.id,
                          label: defaultStatus.name,
                        }
                      : undefined
                  }
                  isDisabled={!currentUser.isSystemAdmin}
                  isLoading={false}
                  isClearable={true}
                  isSearchable={true}
                  isMulti={false}
                  errors={errors.iucnStatus}
                />
                <SelectInput
                  name="populationTrend"
                  label="Select Population Trend"
                  options={populationTrends.map((trend: any) => ({
                    value: trend.id,
                    label: trend.name,
                  }))}
                  onChange={(e: any) =>
                    onChangeSingleSelect("populationTrendId", e)
                  }
                  className="w-full"
                  defaultValue={
                    defaultTrend
                      ? {
                          value: defaultTrend.id,
                          label: defaultTrend.name,
                        }
                      : undefined
                  }
                  isDisabled={!currentUser.isSystemAdmin}
                  isLoading={false}
                  isClearable={true}
                  isSearchable={true}
                  isMulti={false}
                  errors={errors.trend}
                />
                <SelectInput
                  name="speciesCategory"
                  label="Select Species Category"
                  options={speciesCategories.map((category: any) => ({
                    value: category.id,
                    label: category.name,
                  }))}
                  onChange={(e: any) =>
                    onChangeSingleSelect("speciesCategoryId", e)
                  }
                  className="w-full"
                  defaultValue={
                    defaultCategory
                      ? {
                          value: defaultCategory.id,
                          label: defaultCategory.name,
                        }
                      : undefined
                  }
                  isDisabled={!currentUser.isSystemAdmin}
                  isLoading={false}
                  isClearable={true}
                  isSearchable={true}
                  isMulti={false}
                  errors={errors.speciesCategory}
                />
                {currentUser.isSystemAdmin && (
                  <div className="py-2 flex flex-row justify-end">
                    {/* <button
                    className="cursor-pointer p-2 rounded cursor-pointer"
                    onClick={() => onDelete()}
                  >
                    <DeleteIcon />
                  </button> */}

                    <button
                      className="px-3 py-2 rounded bg-deepGreen-500 text-white hover:bg-deepGreen-700 drop-shadow-lg"
                      onClick={() => onCancelEdit()}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit" // Change to submit type to handle form submission
                      className="px-3 py-2 ml-2 rounded bg-deepGreen-500 text-white hover:bg-deepGreen-700 drop-shadow-lg"
                    >
                      Save
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddEditSpecies;
